import { useEffect, useRef, useState } from "react";
import { Loader2, Search, X } from "lucide-react";
import { api } from "@/lib/api";

// GIF picker for Live chat — picks are sent as chat messages (media_type "gif").
// Never renders anything over the stage: it is a panel anchored to the composer.
export default function LiveChatGifPicker({ onPick, disabled }) {
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(null);
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const timer = useRef(null);

  useEffect(() => {
    if (!open || enabled !== null) return;
    api.get("/gifs/status").then(({ data }) => setEnabled(!!data.enabled)).catch(() => setEnabled(false));
  }, [open, enabled]);

  useEffect(() => {
    if (!open || !enabled) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true); setErr("");
      try {
        const { data } = await api.get("/gifs/search", { params: { q, limit: 24 } });
        setItems(data.results || []);
      } catch (e) { setErr(e?.response?.data?.detail || "GIF search failed."); setItems([]); }
      finally { setLoading(false); }
    }, q ? 350 : 0);
    return () => clearTimeout(timer.current);
  }, [q, open, enabled]);

  const pick = (g) => { onPick?.(g); setOpen(false); setQ(""); };

  return (
    <>
      <button type="button" onClick={() => setOpen((v) => !v)} disabled={disabled} aria-label="Send a GIF" data-testid="live-chat-gif-button"
        className={`shrink-0 h-10 w-10 grid place-items-center rounded-full backdrop-blur-md border text-[10px] font-black tracking-wider disabled:opacity-40 ${open ? "bg-rose-600 border-rose-500 text-white" : "bg-black/50 hover:bg-black/70 border-white/15 text-white"}`}>
        GIF
      </button>
      {open && (
        <div data-testid="live-chat-gif-panel" onClick={(e) => e.stopPropagation()}
          className="absolute left-0 bottom-12 z-40 w-[min(92vw,440px)] max-h-[46vh] flex flex-col rounded-2xl bg-zinc-950/95 backdrop-blur-md border border-white/10 shadow-2xl overflow-hidden">
          <div className="flex items-center gap-2 p-2 border-b border-white/10">
            <Search size={14} className="text-zinc-500 shrink-0" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search Tenor GIFs…" data-testid="live-chat-gif-search"
              className="flex-1 bg-transparent text-sm text-white placeholder-zinc-500 focus:outline-none" />
            <button type="button" onClick={() => setOpen(false)} aria-label="Close GIF picker" data-testid="live-chat-gif-close" className="text-zinc-400 hover:text-white"><X size={14} /></button>
          </div>
          <div className="overflow-y-auto p-2">
            {enabled === false && <div className="text-xs text-zinc-400 p-3 text-center" data-testid="live-chat-gif-unavailable">GIF search isn't available yet — the owner needs to add a Tenor key.</div>}
            {enabled && err && <div className="text-xs text-rose-300 p-3 text-center">{err}</div>}
            {enabled && loading && <div className="flex justify-center p-4"><Loader2 size={16} className="animate-spin text-zinc-400" /></div>}
            {enabled && !loading && items.length > 0 && (
              <div className="grid grid-cols-3 gap-1.5" data-testid="live-chat-gif-grid">
                {items.map((g) => (
                  <button key={g.id} type="button" onClick={() => pick(g)} data-testid={`live-chat-gif-item-${g.id}`} className="aspect-square overflow-hidden rounded-lg bg-black/40 hover:ring-2 hover:ring-rose-500">
                    <img src={g.preview} alt={g.title} loading="lazy" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            {enabled && !loading && !err && items.length === 0 && <div className="text-xs text-zinc-500 p-3 text-center">No GIFs found.</div>}
          </div>
          <div className="text-[9px] text-zinc-600 text-right px-2 py-1">Powered by Tenor</div>
        </div>
      )}
    </>
  );
}
